import React, { useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  MobileDateRangePicker,
  MultiInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import useAuth from './useAuth';
import { parseUTC } from 'src/utils/timezones';
import { isEmpty } from 'lodash';
import useGetLocaleTextDatePicker from './useGetLocaleTextDatePicker';
import { formatDatesFromRangeSelector } from './useDateFilterForTable';
import usePageFilterModel from './usePageFilterModel';

const EMPTY_COLUMNS = [];
const DATE_FILTER_IDS = ['date-filter-start', 'date-filter-end'];

const isDateRangeFilter = (item) => DATE_FILTER_IDS.includes(item.id);

export const getGridFilterModel = (filterModel) => ({
  ...filterModel,
  items: (filterModel.items || []).filter((item) => !isDateRangeFilter(item)),
});

export const mergeGridAndDateFilterModels = ({
  gridFilterModel,
  dateFilterModel,
}) => ({
  ...gridFilterModel,
  items: [
    ...(gridFilterModel.items || []).filter((item) => !isDateRangeFilter(item)),
    ...(dateFilterModel.items || []).filter(isDateRangeFilter),
  ],
});

export const applyDateRangeToFilterModel = ({
  filterModel,
  start,
  end,
  startField,
  endField,
  timezoneId,
}) => {
  const items = (filterModel.items || []).filter(
    (item) => !isDateRangeFilter(item)
  );

  if (!start || !end) {
    return { ...filterModel, items };
  }

  const startYMD = moment(start).format('YYYY-MM-DD');
  const endYMD = moment(end).format('YYYY-MM-DD');

  return {
    ...filterModel,
    items: [
      ...items,
      {
        id: 'date-filter-start',
        field: startField,
        operator: 'onOrAfter',
        value: moment
          .tz(startYMD, timezoneId)
          .startOf('day')
          .utc()
          .format('YYYY-MM-DDTHH:mm'),
      },
      {
        id: 'date-filter-end',
        field: endField,
        operator: 'onOrBefore',
        value: moment
          .tz(endYMD, timezoneId)
          .endOf('day')
          .utc()
          .format('YYYY-MM-DDTHH:mm'),
      },
    ],
  };
};

const useDateFilterForTablePersist = ({
  defaultDateRange,
  startField = 'start_date',
  endField = 'end_date',
  columns = EMPTY_COLUMNS,
  pageId,
  /** MUI TextField size for the range inputs (match sibling filters). */
  size = 'medium',
}) => {
  const localeText = useGetLocaleTextDatePicker();
  const location = useLocation();
  const id = pageId ?? location.pathname;
  const { user } = useAuth();
  const { filterModel, handleSetFilterModel: persistFilterModel } =
    usePageFilterModel({
      pageId: id,
    });

  const defaultFilterModel = useMemo(() => {
    const items = [];

    if (defaultDateRange.start != null) {
      items.push({
        id: 'date-filter-start',
        field: startField,
        operator: 'onOrAfter',
        value: moment
          .tz(
            moment(defaultDateRange.start).format('YYYY-MM-DD'),
            user.timezone_id
          )
          .startOf('day')
          .utc()
          .format('YYYY-MM-DDTHH:mm'),
      });
    }

    if (defaultDateRange.end != null) {
      items.push({
        id: 'date-filter-end',
        field: endField,
        operator: 'onOrBefore',
        value: moment
          .tz(
            moment(defaultDateRange.end).format('YYYY-MM-DD'),
            user.timezone_id
          )
          .endOf('day')
          .utc()
          .format('YYYY-MM-DDTHH:mm'),
      });
    }

    return {
      items,
      linkOperator: 'and',
    };
  }, [
    defaultDateRange.start,
    defaultDateRange.end,
    startField,
    endField,
    user.timezone_id,
  ]);
  const dateFields = useMemo(
    () =>
      new Set(
        columns.filter((col) => col.type === 'date').map((col) => col.field)
      ),
    [columns]
  );
  const memoizedFilterModel = useMemo(() => {
    const base = isEmpty(filterModel.items) ? defaultFilterModel : filterModel;
    const linkOperator = base.linkOperator ?? base.logicOperator ?? 'and';
    // eslint-disable-next-line no-unused-vars
    const { logicOperator: _legacyLogicOperator, ...rest } = base;
    if (dateFields.size === 0) {
      return { ...rest, linkOperator };
    }
    return {
      ...rest,
      linkOperator,
      items: (rest.items || []).map((item) => {
        // MUI DataGrid sends date-only column filter values as Date objects or ISO strings.
        // Format to 'YYYY-MM-DD' so the backend receives just the date portion.
        if (
          dateFields.has(item.field) &&
          !['date-filter-start', 'date-filter-end'].includes(item.id) &&
          item.value
        ) {
          return {
            ...item,
            // MUI's date-column filter input works entirely in UTC: it stores
            // the picked day as a UTC-midnight Date (new Date('2025-07-01')) and
            // renders it back via Date.toISOString().substring(0,10). We must
            // extract the date portion in UTC to stay consistent with it.
            // Formatting in local time (moment(value) or new Date(value)) shifts
            // the day backwards for negative UTC offsets (e.g. Pacific time),
            // corrupting both the displayed filter and the query. moment.utc()
            // handles both the Date object and a 'YYYY-MM-DD' string.
            value: moment.utc(item.value).format('YYYY-MM-DD'),
          };
        }
        return item;
      }),
    };
  }, [filterModel, defaultFilterModel, dateFields]);
  const getProcessFilter = useCallback(
    (filter, range) =>
      applyDateRangeToFilterModel({
        filterModel: filter,
        start: range.start,
        end: range.end,
        startField,
        endField,
        timezoneId: user.timezone_id,
      }),
    [endField, startField, user.timezone_id]
  );
  const updateDateRange = useCallback(
    (range) => {
      const { start, end } = formatDatesFromRangeSelector({ range });
      persistFilterModel(getProcessFilter(memoizedFilterModel, { start, end }));
    },
    [getProcessFilter, memoizedFilterModel, persistFilterModel]
  );
  const gridFilterModel = useMemo(
    () => getGridFilterModel(memoizedFilterModel),
    [memoizedFilterModel]
  );
  const handleSetFilterModel = useCallback(
    (nextGridFilterModel) => {
      persistFilterModel(
        mergeGridAndDateFilterModels({
          gridFilterModel: nextGridFilterModel,
          dateFilterModel: memoizedFilterModel,
        })
      );
    },
    [memoizedFilterModel, persistFilterModel]
  );
  const dateRange = useMemo(() => {
    const obj = { start: null, end: null };
    memoizedFilterModel.items.forEach((item) => {
      if (item.id === 'date-filter-start') {
        obj.start = parseUTC(item.value, user);
      } else if (item.id === 'date-filter-end') {
        obj.end = parseUTC(item.value, user);
      }
    });
    return obj;
  }, [memoizedFilterModel.items, user]);
  const startDate = dateRange.start || defaultDateRange.start;
  const endDate = dateRange.end || defaultDateRange.end;
  const pickerKey = `${id}-${startDate?.getTime() ?? 'null'}-${
    endDate?.getTime() ?? 'null'
  }`;

  const DateRangeSelector = (
    <MobileDateRangePicker
      key={pickerKey}
      localeText={localeText}
      defaultValue={[startDate, endDate]}
      onAccept={updateDateRange}
      slots={{ field: MultiInputDateRangeField }}
      slotProps={{
        field: {
          separator: (
            <Box
              component="span"
              sx={{
                mx: 1,
                display: 'inline-flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: size === 'small' ? 13 : 14,
              }}
            >
              to
            </Box>
          ),
        },
        textField: {
          size,
          sx: {
            minWidth: size === 'small' ? 140 : 160,
          },
        },
      }}
    />
  );

  return {
    updateDateRange,
    DateRangeSelector,
    filterModel: memoizedFilterModel,
    gridFilterModel,
    handleSetFilterModel,
    dateRange,
  };
};

export default useDateFilterForTablePersist;
